import { useEffect, useMemo, useState } from "react";
import { LOCATIONS } from "@/config/locations";
import { fetchOrgLocations } from "@/lib/locations/fetchOrgLocations";
import { orgLocationPickerOptions, type OrgLocationPickerOption } from "@/lib/locations/orgLocationPickerOptions";
import { supabase } from "@/lib/supabase";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import type { MemberWithProfile, OrgRole, PlatformRole } from "./adminTypes";
import { AdminActiveMemberRow } from "./AdminActiveMemberRow";

export type AdminActiveMembersBlockProps = {
  activeMembers: MemberWithProfile[];
  currentUserId: string | undefined;
  loading: boolean;
  isPlatformAdmin: boolean;
  canAssignManufacturingOrgRole: boolean;
  collapsedMembers: Set<string>;
  toggleMember: (userId: string) => void;
  platformRoleByUser: Record<string, string>;
  memberPlatformRoleEdits: Record<string, PlatformRole>;
  setMemberPlatformRoleEdits: React.Dispatch<React.SetStateAction<Record<string, PlatformRole>>>;
  memberLocations: Record<string, string[]>;
  addRoleByUser: Record<string, OrgRole>;
  setAddRoleByUser: React.Dispatch<React.SetStateAction<Record<string, OrgRole>>>;
  addLocByUser: Record<string, string>;
  setAddLocByUser: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onUpdateMemberPlatformRole: (member: MemberWithProfile) => void;
  onBlockMember: (userId: string, name: string) => void;
  onDeactivateMembership: (member: MemberWithProfile) => void;
  onDeleteUser: (userId: string, name: string) => void;
  onRemoveOrgRole: (member: MemberWithProfile, role: OrgRole) => void;
  onAddOrgRole: (member: MemberWithProfile, role: OrgRole) => void;
  onAddUserLocation: (member: MemberWithProfile, locId: string) => void;
  onRemoveUserLocation: (member: MemberWithProfile, locId: string) => void;
  onResendInvite: (member: MemberWithProfile) => void;
  memberCapabilityByUser: Record<string, import("@/lib/capabilities/capabilityOverrides").CapabilityOverrides>;
  onSetMemberAdsRunner: (member: MemberWithProfile, value: boolean) => void;
  memberPlants?: Record<string, string[]>;
  plantOptions?: import("./AdminMemberPlantAssignments").AdminPlantOption[];
  addPlantByUser?: Record<string, string>;
  setAddPlantByUser?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onAddUserPlant?: (member: MemberWithProfile, plantId: string) => void;
  onRemoveUserPlant?: (member: MemberWithProfile, plantId: string) => void;
};

export function AdminActiveMembersBlock(props: AdminActiveMembersBlockProps) {
  const {
    activeMembers,
    currentUserId,
    loading,
    isPlatformAdmin,
    canAssignManufacturingOrgRole,
    collapsedMembers,
    toggleMember,
    platformRoleByUser,
    memberPlatformRoleEdits,
    setMemberPlatformRoleEdits,
    memberLocations,
    addRoleByUser,
    setAddRoleByUser,
    addLocByUser,
    setAddLocByUser,
    onUpdateMemberPlatformRole,
    onBlockMember,
    onDeactivateMembership,
    onDeleteUser,
    onRemoveOrgRole,
    onAddOrgRole,
    onAddUserLocation,
    onRemoveUserLocation,
    onResendInvite,
  memberCapabilityByUser,
  onSetMemberAdsRunner,
  memberPlants = {},
  plantOptions = [],
  addPlantByUser = {},
  setAddPlantByUser,
  onAddUserPlant,
  onRemoveUserPlant,
  } = props;

  const orgId = useScopedOrganizationId();
  const [orgLocRows, setOrgLocRows] = useState<Array<{ id: string; name: string; city?: string | null }>>([]);
  useEffect(() => {
    if (!orgId || !supabase) {
      setOrgLocRows([]);
      return;
    }
    let alive = true;
    void fetchOrgLocations(supabase, orgId).then(({ data }) => {
      if (!alive) return;
      setOrgLocRows(data.map((r) => ({ id: r.id, name: r.name, city: r.city })));
    });
    return () => {
      alive = false;
    };
  }, [orgId]);
  const locationOptions: OrgLocationPickerOption[] = useMemo(
    () => orgLocationPickerOptions(orgLocRows, LOCATIONS),
    [orgLocRows]
  );

  const [visibleCount, setVisibleCount] = useState<5 | 10 | 20>(10);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(activeMembers.length / visibleCount));
  const visibleMembers = useMemo(() => {
    const start = (currentPage - 1) * visibleCount;
    return activeMembers.slice(start, start + visibleCount);
  }, [activeMembers, currentPage, visibleCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [visibleCount, activeMembers.length]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  if (activeMembers.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-1">
        No members loaded. Run <span className="font-mono">supabase_admin_onboarding.sql</span> to enable this view.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-xs text-gray-600 inline-flex items-center gap-1">
          Rows
          <select
            className="rounded-md border border-gray-300 px-1.5 py-1 text-xs bg-white"
            value={visibleCount}
            onChange={(e) => {
              setVisibleCount(Number(e.target.value) as 5 | 10 | 20);
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </label>
        <div className="text-xs text-gray-600 inline-flex items-center gap-2">
          <span>
            Showing {activeMembers.length === 0 ? 0 : (currentPage - 1) * visibleCount + 1}-
            {Math.min(currentPage * visibleCount, activeMembers.length)} of {activeMembers.length}
          </span>
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-2 py-1 disabled:opacity-50"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ← Prev
          </button>
          <span>
            Page {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-2 py-1 disabled:opacity-50"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            Next →
          </button>
        </div>
      </div>
      {visibleMembers.map((m) => {
        const isSelf = m.user_id === currentUserId;
        const isCollapsed = collapsedMembers.has(m.user_id);
        const currentPlatformRole = platformRoleByUser[m.user_id] ?? "";
        const editedPlatformRole = memberPlatformRoleEdits[m.user_id] ?? (currentPlatformRole as PlatformRole);
        const isPlatformDirty = editedPlatformRole !== currentPlatformRole;
        const signInDate = m.last_sign_in_at ? new Date(m.last_sign_in_at).toLocaleDateString() : "never";
        const userLocs = memberLocations[m.user_id] ?? [];
        const userPlantIds = memberPlants[m.user_id] ?? [];

        return (
          <AdminActiveMemberRow
            key={m.user_id}
            m={m}
            isSelf={isSelf}
            isCollapsed={isCollapsed}
            loading={loading}
            isPlatformAdmin={isPlatformAdmin}
            canAssignManufacturingOrgRole={canAssignManufacturingOrgRole}
            currentPlatformRole={currentPlatformRole}
            editedPlatformRole={editedPlatformRole}
            isPlatformDirty={isPlatformDirty}
            signInDate={signInDate}
            userLocs={userLocs}
            locationOptions={locationOptions}
            toggleMember={toggleMember}
            setMemberPlatformRoleEdits={setMemberPlatformRoleEdits}
            addRoleByUser={addRoleByUser}
            setAddRoleByUser={setAddRoleByUser}
            addLocByUser={addLocByUser}
            setAddLocByUser={setAddLocByUser}
            onUpdateMemberPlatformRole={onUpdateMemberPlatformRole}
            onBlockMember={onBlockMember}
            onDeactivateMembership={onDeactivateMembership}
            onDeleteUser={onDeleteUser}
            onRemoveOrgRole={onRemoveOrgRole}
            onAddOrgRole={onAddOrgRole}
            onAddUserLocation={onAddUserLocation}
            onRemoveUserLocation={onRemoveUserLocation}
            onResendInvite={onResendInvite}
            memberCapabilities={memberCapabilityByUser[m.user_id]}
            onSetMemberAdsRunner={onSetMemberAdsRunner}
            userPlantIds={userPlantIds}
            plantOptions={plantOptions}
            addPlantByUser={addPlantByUser}
            setAddPlantByUser={setAddPlantByUser}
            onAddUserPlant={onAddUserPlant}
            onRemoveUserPlant={onRemoveUserPlant}
          />
        );
      })}
    </div>
  );
}
